import gql from 'graphql-tag';
import apolloClient from '../apollo';

/**
 * State
 */
const getDefaultState = () => {
  return {
    itemTransactions: [],
    itemTransactionCount: 0,
    consumedItems: [],
    consumedItemsCount: 0,
  };
};
const state = getDefaultState();

/**
 * Defines the fields which are requested for an itemTransaction
 * used for all queries which return an itemTransaction
 * @type {DocumentNode}
 */
const ITEM_TRANSACTION_REQUEST_FIELDS = gql`
  fragment itemTransactionFields on ItemTransaction {
    _id
    createdAt
    updatedAt
    quantity
    type
    createdBy {
      _id
      firstname
      lastname
      email
    }
    item {
      number
      description
    }
    fromProject {
      number
      customer {
        lastname
        firstname
      }
    }
    toProject {
      number
      customer {
        lastname
        firstname
      }
    }
    fromStorage {
      number
      name
      itemStorageType
    }
    toStorage {
      number
      name
      itemStorageType
    }
  }
`;

/**
 * Defines the fields which are requested for a consumed item
 * used for all queries which return consumed items
 * @type {DocumentNode}
 */
const CONSUMED_ITEM_REQUEST_FIELDS = gql`
  fragment consumedItemFields on ConsumedItemByProject {
    project {
      id
      number
      customer {
        lastname
        firstname
      }
    }
    item {
      _id
      number
      description
    }
    totalQuantityConsumed
  }
`;

/**
 * Actions
 */
const actions = {
  /**
   * Fetch a paginated, sorted and filtered itemTransaction list
   * @param {*} param0
   * @param {*} payload
   */
  async fetchItemTransactionsPaginated({ commit, dispatch }, { pagination, sorting, filters }) {
    try {
      const response = await apolloClient.query({
        query: gql`
          query itemTransactionsPaginated(
            $pagination: PaginationInput
            $sorting: SortingInput
            $filters: ItemTransactionFilterInput
          ) {
            itemTransactionsPaginated(
              pagination: $pagination
              sorting: $sorting
              filters: $filters
            ) {
              itemTransactions {
                ...itemTransactionFields
              }
              itemTransactionCount
            }
          }
          ${ITEM_TRANSACTION_REQUEST_FIELDS}
        `,
        variables: {
          pagination: pagination,
          sorting: sorting,
          filters: filters,
        },
        fetchPolicy: 'network-only',
      });
      commit('SET_ITEM_TRANSACTIONS', response.data.itemTransactionsPaginated.itemTransactions);
      commit(
        'SET_ITEM_TRANSACTION_COUNT',
        response.data.itemTransactionsPaginated.itemTransactionCount
      );
    } catch (err) {
      console.log(err);
      throw new Error(err);
    }
  },
  /**
   * Fetch a paginated, sorted and filtered consumed items list for a project
   * @param {*} param0
   * @param {*} payload
   */
  async fetchConsumedItemsByProjectPaginated(
    { commit, dispatch },
    { projectId, pagination, sorting, filters }
  ) {
    try {
      const response = await apolloClient.query({
        query: gql`
          query consumedItemsByProjectPaginated(
            $projectId: ID!
            $pagination: PaginationInput
            $sorting: SortingInput
            $filters: ConsumedItemsFilterInput
          ) {
            consumedItemsByProjectPaginated(
              projectId: $projectId
              pagination: $pagination
              sorting: $sorting
              filters: $filters
            ) {
              consumedItems {
                ...consumedItemFields
              }
              consumedItemsCount
            }
          }
          ${CONSUMED_ITEM_REQUEST_FIELDS}
        `,
        variables: {
          projectId: projectId,
          pagination: pagination,
          sorting: sorting,
          filters: filters,
        },
        fetchPolicy: 'network-only',
      });
      commit('SET_CONSUMED_ITEMS', response.data.consumedItemsByProjectPaginated.consumedItems);
      commit(
        'SET_CONSUMED_ITEMS_COUNT',
        response.data.consumedItemsByProjectPaginated.consumedItemsCount
      );
    } catch (err) {
      console.log(err);
      throw new Error(err);
    }
  },
};

/**
 * Mutations
 */
const mutations = {
  SET_DEFAULT_STATE: (state) => {
    Object.assign(state, getDefaultState());
  },
  SET_ITEM_TRANSACTIONS: (state, itemTransactions) =>
    itemTransactions ? (state.itemTransactions = itemTransactions) : (state.itemTransactions = []),
  SET_ITEM_TRANSACTION_COUNT: (state, itemTransactionCount) =>
    (state.itemTransactionCount = itemTransactionCount),
  SET_CONSUMED_ITEMS: (state, consumedItems) =>
    consumedItems ? (state.consumedItems = consumedItems) : (state.consumedItems = []),
  SET_CONSUMED_ITEMS_COUNT: (state, consumedItemsCount) =>
    (state.consumedItemsCount = consumedItemsCount),
};

/**
 * Getters
 */
const getters = {
  getItemTransactions: (state) => state.itemTransactions,
  getItemTransaction: (state) => (itemTransactionId) =>
    state.itemTransactions.find((itemTransaction) => itemTransaction._id == itemTransactionId),
  getItemTransactionCount: (state) => state.itemTransactionCount,
  getConsumedItems: (state) => state.consumedItems,
  getConsumedItemsCount: (state) => state.consumedItemsCount,
};

export default {
  state,
  getters,
  actions,
  mutations,
};
